import React from 'react';
import { Alert, Form, Right, formWithValidation } from '@edgebox/react-components';
import { ISerializedReference, ISerializedRevisionReference, InternalId } from '@edgebox/data-definition-kit';
import {
  ISerializedTermAssignment,
  SyncCoreDataDefinitionsEnumTranslator,
  ISyncCoreMonitoringPromptDTO,
  ISyncCoreSerializedMonitoringPromptProperties,
  MonitoringPromptCountry,
  MonitoringPromptStatus,
  TermAssignmentSourceType,
  MonitoringPromptType,
  TaxonomyFacet,
} from '@edgebox/sync-core-data-definitions';
import { ClientMonitoringPromptDraft, ClientMonitoringPromptEntity, ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { IChipSelectOption } from './ChipsDropdownSelect';
import { EngineLogo } from './EngineLogo';
import { ITaxonomyDropdownOption, ITaxonomyTermDropdownOption, TaxonomyTermTwoStepDropdown } from './TaxonomyTermTwoStepDropdown';
import { OFFERED_EXTRACTION_TARGET_FACETS, formatFacetNameList, heldExtractedByDefaultFacets } from './extractionFacets';
import { TaxonomyTermNameBadge } from '../Onboarding/ContentInventory/Components/TaxonomyTermNameBadge';
import { BP_TOOLBAR_BUTTON_SMALL_CLASSES } from './brandPresenceClasses';

export interface IMonitoringTermOption {
  id: InternalId;
  key: string;
  name: string;
  facet?: TaxonomyFacet;
  taxonomyId?: string;
  taxonomyKey?: string;
}

export type { ITaxonomyDropdownOption };

const ADMINISTRATIVE_TERM_FACETS: TaxonomyFacet[] = [
  TaxonomyFacet.Competitor,
  TaxonomyFacet.CompetitorProduct,
  TaxonomyFacet.AuthorityTopic,
  TaxonomyFacet.AuthorityTopicCategory,
  TaxonomyFacet.Site,
  TaxonomyFacet.Initiative,
  TaxonomyFacet.Brand,
  TaxonomyFacet.Product,
  TaxonomyFacet.ProductFeature,
  TaxonomyFacet.Market,
  TaxonomyFacet.TargetIndustry,
  TaxonomyFacet.AcademicUnit,
  TaxonomyFacet.CompetitorProgram,
  TaxonomyFacet.Credential,
  TaxonomyFacet.Incident,
  TaxonomyFacet.AcademicLevel,
  TaxonomyFacet.Persona,
  TaxonomyFacet.FunnelStage,
  TaxonomyFacet.Intent,
];

const STATIC_TERM_FACETS: TaxonomyFacet[] = [
  TaxonomyFacet.AuthorityTopic,
  TaxonomyFacet.AuthorityTopicCategory,
  TaxonomyFacet.Site,
  TaxonomyFacet.Initiative,
  TaxonomyFacet.Market,
  TaxonomyFacet.TargetIndustry,
  TaxonomyFacet.AcademicUnit,
  TaxonomyFacet.Credential,
  TaxonomyFacet.Incident,
  TaxonomyFacet.AcademicLevel,
  TaxonomyFacet.Persona,
  TaxonomyFacet.FunnelStage,
  TaxonomyFacet.Intent,
];

const PURPOSE_TERM_FACETS: TaxonomyFacet[] = [TaxonomyFacet.MonitoringPromptPurpose];

type IMonitoringPromptDraftFormValues = ISyncCoreSerializedMonitoringPromptProperties;

/**
 * The form's view of a prompt's values.
 *
 * `templateFacet` admits `null` here because CLEARING IT MUST SEND null: `JSON.stringify` drops an
 * undefined key, and the server merges the payload over the stored prompt, so an omitted field
 * reads as "unchanged" and the facet would never clear. Both the update DTO and the shared draft
 * declare it as an optional enum, which accepts null — so this widening describes what the wire
 * already allows rather than inventing a shape the server would reject.
 *
 * Each union member is rebuilt rather than intersected: an intersection narrows a property to the
 * stricter of the two sides, which would take `null` straight back out again.
 */
type IMonitoringPromptFormValues =
  | (Omit<ISyncCoreSerializedMonitoringPromptProperties, 'templateFacet'> & { templateFacet?: TaxonomyFacet | null })
  | (Omit<ISyncCoreMonitoringPromptDTO, 'templateFacet'> & { templateFacet?: TaxonomyFacet | null });
/**
 * One locale/country row as the form edits it. `localeRef` is the ACTUAL reference — kept even when
 * it does not resolve in the currently loaded locale options, so a pair whose option has not loaded
 * yet is never rebuilt (and thereby lost) from a resolved id alone.
 */
type ILocaleCountryPairDraft = {
  key: string;
  localeRef?: ISerializedRevisionReference;
  country: MonitoringPromptCountry | '';
};

const MonitoringPromptDraftForm = formWithValidation<IMonitoringPromptDraftFormValues, ClientMonitoringPromptDraft>(
  (values) => plainToInstance(ClientMonitoringPromptDraft, values),
  (entity) => instanceToPlain(entity) as IMonitoringPromptDraftFormValues
);

const MonitoringPromptEntityForm = formWithValidation<ISyncCoreMonitoringPromptDTO, ClientMonitoringPromptEntity>(
  (values) => plainToInstance(ClientMonitoringPromptEntity, values),
  (entity) => instanceToPlain(entity) as ISyncCoreMonitoringPromptDTO
);

const ALL_COUNTRIES = Object.values(MonitoringPromptCountry);
const TEMPLATE_FACETS = Object.values(TaxonomyFacet).filter((facet) => facet !== TaxonomyFacet.Locale && facet !== TaxonomyFacet.Engine);

type TMonitoringPromptFormEnumName = 'MonitoringPromptType' | 'MonitoringPromptStatus' | 'MonitoringPromptCountry' | 'TaxonomyFacet';

function translateEnumValue(enumName: TMonitoringPromptFormEnumName, value: string): string {
  try {
    return SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue(enumName, value as any);
  } catch {
    return value;
  }
}

function revisionReferenceFromOption(option?: IMonitoringTermOption): ISerializedRevisionReference | undefined {
  if (!option) {
    return undefined;
  }

  return {
    _: 1,
    id: option.id,
    key: option.key,
  };
}

function getTermReferenceFromAssignment(assignment: unknown): ISerializedRevisionReference | undefined {
  if (!assignment || typeof assignment !== 'object') {
    return undefined;
  }

  const assignmentRecord = assignment as { term?: ISerializedRevisionReference };
  return assignmentRecord.term;
}

function termAssignmentFromOption(option: IMonitoringTermOption): ISerializedTermAssignment | undefined {
  if (!option.taxonomyId || !option.facet) {
    return undefined;
  }

  return {
    taxonomy: { _: 1, id: option.taxonomyId } as ISerializedReference,
    term: {
      _: 1,
      id: option.id,
      key: option.key,
    },
    source: {
      type: TermAssignmentSourceType.User,
    },
    facet: option.facet,
  };
}

function getSubmitErrorMessage(error: unknown): string {
  if (!error) return '';

  const errorRecord = error as { response?: { data?: unknown } };
  const responseData = errorRecord.response?.data;
  const payload = responseData ?? error;

  if (payload && typeof payload === 'object') {
    const payloadRecord = payload as { message?: unknown };
    const message = payloadRecord.message;
    if (Array.isArray(message)) {
      return message
        .map((entry) => {
          if (typeof entry === 'string') {
            return entry;
          }

          const entryRecord = entry as { property?: string; constraints?: Record<string, string> };
          const property = entryRecord.property;
          const constraints = entryRecord.constraints;
          const constraintText = constraints && typeof constraints === 'object' ? Object.values(constraints).join(' ') : undefined;
          return property && constraintText ? `${property}: ${constraintText}` : property || JSON.stringify(entry);
        })
        .join('\n');
    }

    if (typeof message === 'string' && message) {
      return message;
    }
  }

  return error instanceof Error ? error.message : String(error);
}

function hasRevisionReference(reference: unknown): boolean {
  if (!reference || typeof reference !== 'object') {
    return false;
  }

  const referenceRecord = reference as {
    id?: unknown;
    key?: unknown;
    getId?: () => unknown;
    getStableKey?: () => unknown;
  };

  if (referenceRecord.getId?.()) {
    return true;
  }

  if (referenceRecord.getStableKey?.()) {
    return true;
  }

  if (referenceRecord.id) {
    return true;
  }

  return !!referenceRecord.key;
}

function getRevisionReferenceId(reference: unknown): string {
  if (!reference || typeof reference !== 'object') {
    return '';
  }

  const referenceRecord = reference as {
    id?: unknown;
    getId?: () => unknown;
  };

  return String(referenceRecord.getId?.() || referenceRecord.id || '');
}

function getRevisionReferenceKey(reference: unknown): string {
  if (!reference || typeof reference !== 'object') {
    return '';
  }

  const referenceRecord = reference as {
    key?: unknown;
    getStableKey?: () => unknown;
  };

  return String(referenceRecord.getStableKey?.() || referenceRecord.key || '');
}

function getOptionIdForReference(
  reference: ISerializedRevisionReference | undefined,
  optionsById: Map<string, IMonitoringTermOption>,
  optionsByKey: Map<string, IMonitoringTermOption>
): string {
  const id = getRevisionReferenceId(reference);
  if (id && optionsById.has(id)) {
    return id;
  }

  const key = getRevisionReferenceKey(reference);
  if (!key) {
    return '';
  }

  const option = optionsByKey.get(key);
  return option ? String(option.id) : '';
}

/**
 * Removes the one entry whose reference resolves to `idToRemove`, leaving every other entry in
 * `entries` exactly as stored — including one whose own reference does not resolve to any id
 * because its option has not loaded, is paged out, or is archived.
 *
 * The alternative — rebuilding the whole array from a "selected ids" list — is the trap this
 * replaces: that list is already filtered down to entries that resolve, for display, so writing it
 * back on the next add or remove would silently drop anything unresolved even though the user never
 * touched it.
 */
function removeEntryById<TEntry>(
  entries: TEntry[],
  idToRemove: string,
  getReference: (entry: TEntry) => ISerializedRevisionReference | undefined,
  optionsById: Map<string, IMonitoringTermOption>,
  optionsByKey: Map<string, IMonitoringTermOption>
): TEntry[] {
  return entries.filter((entry) => getOptionIdForReference(getReference(entry), optionsById, optionsByKey) !== idToRemove);
}

function areRevisionReferencesEqual(
  left: ISerializedRevisionReference | undefined,
  right: ISerializedRevisionReference | undefined
): boolean {
  return getRevisionReferenceId(left) === getRevisionReferenceId(right) && getRevisionReferenceKey(left) === getRevisionReferenceKey(right);
}

function getExpectedTemplatePlaceholder(templateFacet?: TaxonomyFacet | null): string | undefined {
  if (!templateFacet) {
    return undefined;
  }

  const snakeCaseFacet = String(templateFacet)
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();

  return `{{${snakeCaseFacet}}}`;
}

function assignmentFacet(assignment: unknown): TaxonomyFacet | undefined {
  if (!assignment || typeof assignment !== 'object') {
    return undefined;
  }

  return (assignment as { facet?: TaxonomyFacet }).facet;
}

/**
 * How often a prompt runs, as the reader thinks about it.
 *
 * Hours are what the backend stores and schedules on, but nobody schedules monitoring in hours —
 * they schedule it in "daily" or "twice a day". The label carries the intent and the value carries
 * the unit.
 *
 * A prompt whose stored value is not in this table keeps that value and is shown as a custom
 * entry. Rewriting it to the nearest option would change how often a customer is measured because
 * they opened a form, which is the defect this selector replaces.
 */
const MONITORING_PROMPT_FREQUENCY_OPTIONS: ReadonlyArray<{ hours: number; label: string }> = [
  { hours: 168, label: 'Weekly' },
  { hours: 72, label: 'Every 3 days' },
  { hours: 48, label: 'Every 2 days' },
  { hours: 24, label: 'Daily' },
  { hours: 12, label: 'Twice daily' },
  { hours: 8, label: '3x daily' },
  { hours: 6, label: '4x daily' },
];

/** The rate a new prompt starts on, and the only value this form ever fills in by itself. */
const MONITORING_PROMPT_DEFAULT_FREQUENCY_HOURS = 24;

/**
 * Placeholder `<option>` value for a locale/country row whose locale reference is real but does not
 * resolve in the currently loaded options. Distinct from `''` (the "Select locale" placeholder)
 * specifically so the two never collide as two options sharing one value — which would leave the
 * browser to pick one arbitrarily and could render a kept pair as if nothing were selected.
 */
const UNRESOLVED_LOCALE_OPTION_VALUE = '__unresolved-locale__';

type MonitoringPromptFormErrors = Partial<Record<keyof ISyncCoreSerializedMonitoringPromptProperties, string>>;

/**
 * Custom-rule validator passed to `formWithValidation` via the `validate` prop.
 *
 * Field-level rules (name, prompt text, prompt locale, frequency) are enforced
 * automatically by class-validator on the draft/entity classes, so we only
 * cover the cross-field/array rules class-validator cannot express here —
 * including the mandatory `MonitoringPromptPurpose` assignment.
 */
function validateMonitoringPromptForm(values: {
  type?: MonitoringPromptType;
  engines?: unknown[];
  countryLocales?: Array<{ country?: unknown; locale?: unknown }>;
  terms?: unknown[];
}): MonitoringPromptFormErrors | undefined {
  const errors: MonitoringPromptFormErrors = {};
  const isTemplate = values.type === MonitoringPromptType.Template;
  const isInstance = values.type === MonitoringPromptType.Instance;
  const countryLocales = values.countryLocales || [];

  if (!(values.engines || []).length) {
    errors.engines = 'At least one AI engine is required.';
  }

  if (isTemplate && !countryLocales.length) {
    errors.countryLocales = 'At least one locale/country pair is required.';
  } else if (countryLocales.some((entry) => !entry?.country || !hasRevisionReference(entry?.locale))) {
    errors.countryLocales = 'Each locale/country pair must include both locale and country.';
  } else if (isInstance && countryLocales.length !== 1) {
    errors.countryLocales = 'Monitoring prompt instances must have exactly one locale/country pair.';
  }

  if (!(values.terms || []).some((assignment) => assignmentFacet(assignment) === TaxonomyFacet.MonitoringPromptPurpose)) {
    errors.terms = 'At least one prompt purpose is required.';
  }

  return Object.keys(errors).length ? errors : undefined;
}

/**
 * The form body.
 *
 * IT DOES NOT WRITE `status`. There is no status control here, so the form's job is to carry the
 * loaded status through untouched — a paused prompt that is opened and saved stays paused. A
 * render effect used to force `Active`, which silently re-activated whatever was opened, and for a
 * template the reconcile then pushed that status onto every instance it manages. Defaults belong
 * in `ensureMonitoringPromptDraftDefaults`, which fills an ABSENT status rather than replacing a
 * present one.
 */
export function PromptFormBody({
  api,
  values,
  initialPromptText,
  setValue,
  taxonomyOptions,
  localeOptions,
  availableCountries,
  engineOptions,
  templateTermOptions,
  submitLabel,
  saving,
  lockType,
}: {
  api?: ISyncCoreApi;
  values: IMonitoringPromptFormValues;
  initialPromptText?: string;
  setValue: <TPath extends keyof IMonitoringPromptFormValues>(path: TPath, value: IMonitoringPromptFormValues[TPath] | undefined) => void;
  taxonomyOptions: ITaxonomyDropdownOption[];
  localeOptions: IMonitoringTermOption[];
  availableCountries: MonitoringPromptCountry[];
  engineOptions: IMonitoringTermOption[];
  templateTermOptions: IMonitoringTermOption[];
  submitLabel: string;
  saving: boolean;
  lockType?: boolean;
}) {
  const localeOptionsById = React.useMemo(() => new Map(localeOptions.map((option) => [String(option.id), option])), [localeOptions]);
  const localeOptionsByKey = React.useMemo(() => new Map(localeOptions.map((option) => [option.key, option])), [localeOptions]);
  const engineOptionsById = React.useMemo(() => new Map(engineOptions.map((option) => [String(option.id), option])), [engineOptions]);
  const engineOptionsByKey = React.useMemo(() => new Map(engineOptions.map((option) => [option.key, option])), [engineOptions]);
  const templateTermOptionsById = React.useMemo(
    () => new Map(templateTermOptions.map((option) => [String(option.id), option])),
    [templateTermOptions]
  );
  const templateTermOptionsByKey = React.useMemo(
    () => new Map(templateTermOptions.map((option) => [option.key, option])),
    [templateTermOptions]
  );
  const selectedTemplateFacet = values.templateFacet;
  const templatePlaceholder = getExpectedTemplatePlaceholder(selectedTemplateFacet);
  const templateGuidanceText =
    selectedTemplateFacet && templatePlaceholder ? `Use placeholder ${templatePlaceholder} in Prompt text.` : undefined;

  const buildAllowedTaxonomyOptions = React.useCallback(
    (allowedFacets: TaxonomyFacet[]) => {
      const allowedFacetSet = new Set(allowedFacets);
      return taxonomyOptions.filter((taxonomy) => allowedFacetSet.has(taxonomy.facet));
    },
    [taxonomyOptions]
  );

  const buildAllowedTermOptions = React.useCallback(
    (allowedFacets: TaxonomyFacet[]): ITaxonomyTermDropdownOption[] => {
      const allowedFacetSet = new Set(allowedFacets);
      return templateTermOptions
        .filter((option) => !!option.facet && allowedFacetSet.has(option.facet))
        .map((option) => ({
          id: String(option.id),
          key: option.key,
          name: option.name,
          facet: option.facet,
          taxonomyKey: option.taxonomyKey,
        }));
    },
    [templateTermOptions]
  );

  const administrativeTaxonomies = React.useMemo(
    () => buildAllowedTaxonomyOptions(ADMINISTRATIVE_TERM_FACETS),
    [buildAllowedTaxonomyOptions]
  );
  const staticTaxonomies = React.useMemo(() => buildAllowedTaxonomyOptions(STATIC_TERM_FACETS), [buildAllowedTaxonomyOptions]);
  const targetTaxonomies = React.useMemo(
    () => buildAllowedTaxonomyOptions(OFFERED_EXTRACTION_TARGET_FACETS),
    [buildAllowedTaxonomyOptions]
  );
  const purposeTaxonomies = React.useMemo(() => buildAllowedTaxonomyOptions(PURPOSE_TERM_FACETS), [buildAllowedTaxonomyOptions]);

  const administrativeTermOptions = React.useMemo(() => buildAllowedTermOptions(ADMINISTRATIVE_TERM_FACETS), [buildAllowedTermOptions]);
  const staticTermOptions = React.useMemo(() => buildAllowedTermOptions(STATIC_TERM_FACETS), [buildAllowedTermOptions]);
  const targetTermOptions = React.useMemo(() => buildAllowedTermOptions(OFFERED_EXTRACTION_TARGET_FACETS), [buildAllowedTermOptions]);

  // The extracted-by-default vocabularies this account holds, named in the hint under the field so
  // the customer knows those tags are read from every answer already and need not be attached here.
  const extractionHintNames = React.useMemo(
    () =>
      heldExtractedByDefaultFacets(
        taxonomyOptions.map((taxonomy) => taxonomy.facet).filter((facet): facet is TaxonomyFacet => !!facet)
      ).map((facet) => translateEnumValue('TaxonomyFacet', facet)),
    [taxonomyOptions]
  );
  const purposeTermOptions = React.useMemo(() => buildAllowedTermOptions(PURPOSE_TERM_FACETS), [buildAllowedTermOptions]);
  const templateTermFacetSet = React.useMemo(
    () => new Set(templateTermOptions.map((option) => option.facet).filter((facet): facet is TaxonomyFacet => !!facet)),
    [templateTermOptions]
  );
  const templateTermTaxonomies = React.useMemo(
    () => taxonomyOptions.filter((taxonomy) => templateTermFacetSet.has(taxonomy.facet)),
    [taxonomyOptions, templateTermFacetSet]
  );
  const templateTermDropdownOptions = React.useMemo(
    () =>
      templateTermOptions.map((option) => ({
        id: String(option.id),
        key: option.key,
        name: option.name,
        facet: option.facet,
        taxonomyKey: option.taxonomyKey,
      })),
    [templateTermOptions]
  );
  const countrySelectOptions = React.useMemo<IChipSelectOption<MonitoringPromptCountry>[]>(
    () =>
      availableCountries.map((country) => ({
        value: country,
        label: translateEnumValue('MonitoringPromptCountry', country),
      })),
    [availableCountries]
  );

  const promptLocaleId = getOptionIdForReference(values.promptLocale, localeOptionsById, localeOptionsByKey);
  const isTemplate = values.type === MonitoringPromptType.Template;
  const isInstance = values.type === MonitoringPromptType.Instance;
  const templatePromptTextChanged = isTemplate && initialPromptText !== undefined && (values.promptText || '') !== initialPromptText;
  const isTemplateManagedInstance =
    values.type === MonitoringPromptType.Instance && hasRevisionReference((values as { templatePrompt?: unknown }).templatePrompt);
  const [templateLocaleCountryPairs, setTemplateLocaleCountryPairs] = React.useState<ILocaleCountryPairDraft[]>(
    // A brand-new draft has no pairs to show yet, so seed one blank row for a first paint. Evaluated
    // once against the values this component mounted with: countryLocales itself is never behind an
    // async fetch (only the option lists used to DISPLAY it are), so this can never discard anything
    // that was actually loaded — unlike the render effect it replaces, it never runs again once an
    // option list finishes loading.
    () => ((values.countryLocales || []).length ? [] : [{ key: 'initial-pair', localeRef: undefined, country: '' }])
  );
  const pendingPairKeyRef = React.useRef(0);
  const nextPendingPairKey = React.useCallback(() => {
    pendingPairKeyRef.current += 1;
    return `new-pair-${pendingPairKeyRef.current}`;
  }, []);
  // A legacy or API-set value the table does not offer. Kept and labelled rather than rewritten.
  const isCustomFrequency =
    typeof values.frequencyHours === 'number' &&
    Number.isFinite(values.frequencyHours) &&
    !MONITORING_PROMPT_FREQUENCY_OPTIONS.some((option) => option.hours === values.frequencyHours);

  React.useEffect(() => {
    if (isTemplate) {
      return;
    }

    const promptLocale = values.promptLocale;
    const currentCountryLocales = values.countryLocales || [];
    if (!promptLocale) {
      if (currentCountryLocales.length) {
        setValue('countryLocales', []);
      }
      return;
    }

    const fallbackCountry = currentCountryLocales[0]?.country;
    const country = fallbackCountry;

    if (!country) {
      if (currentCountryLocales.length) {
        setValue('countryLocales', []);
      }
      return;
    }

    const nextCountryLocales: ISyncCoreSerializedMonitoringPromptProperties['countryLocales'] = [
      {
        country,
        locale: promptLocale,
      },
    ];

    if (
      currentCountryLocales.length === 1 &&
      currentCountryLocales[0]?.country === country &&
      areRevisionReferencesEqual(currentCountryLocales[0]?.locale, promptLocale)
    ) {
      return;
    }

    setValue('countryLocales', nextCountryLocales);
  }, [isTemplate, setValue, values.countryLocales, values.promptLocale]);

  /**
   * The persisted pairs, resolved against the currently loaded locale options for DISPLAY only.
   *
   * NOTHING here is filtered out. The render effect this replaces mirrored countryLocales into local
   * state and only kept a pair whose locale resolved in the loaded option map, so a pair whose
   * option had not loaded yet — still fetching, paged out, or archived — resolved to no id, was
   * dropped from the mirror, and the SAME effect then wrote the shrunken mirror back over
   * countryLocales: a pair could be lost without the user touching this form at all. Resolving live
   * off values.countryLocales instead of a stale mirror also means a pair whose option finishes
   * loading a moment later starts showing its real name on the very next render, with no effect
   * required to notice.
   */
  const existingLocaleCountryPairs = React.useMemo(() => {
    const entries = isInstance ? (values.countryLocales || []).slice(0, 1) : values.countryLocales || [];
    return entries.map((entry, entryIndex) => ({
      entryIndex,
      localeRef: entry.locale as ISerializedRevisionReference | undefined,
      localeId: getOptionIdForReference(entry.locale, localeOptionsById, localeOptionsByKey),
      country: entry.country as MonitoringPromptCountry | '',
    }));
  }, [isInstance, localeOptionsById, localeOptionsByKey, values.countryLocales]);

  // Rows not yet complete enough to belong in countryLocales (a fresh "Add pair" row, or an existing
  // pair the user is mid-edit on). An instance never shows more than one row of either kind.
  const visiblePendingLocaleCountryPairs = isInstance ? templateLocaleCountryPairs.slice(0, 1) : templateLocaleCountryPairs;

  const displayLocaleCountryPairRows = React.useMemo(() => {
    const existingRows = existingLocaleCountryPairs.map((pair) => ({
      key: `existing-pair-${pair.entryIndex}`,
      localeRef: pair.localeRef,
      localeId: pair.localeId,
      country: pair.country,
      source: { kind: 'existing' as const, entryIndex: pair.entryIndex },
    }));

    const pendingRows = visiblePendingLocaleCountryPairs.map((pair) => ({
      key: pair.key,
      localeRef: pair.localeRef,
      localeId: pair.localeRef ? getOptionIdForReference(pair.localeRef, localeOptionsById, localeOptionsByKey) : '',
      country: pair.country,
      source: { kind: 'pending' as const, key: pair.key },
    }));

    return [...existingRows, ...pendingRows];
  }, [existingLocaleCountryPairs, localeOptionsById, localeOptionsByKey, visiblePendingLocaleCountryPairs]);

  // A row whose reference is real but does not resolve in the loaded options: not wrong, just
  // unknown right now. Named here rather than silently edited or erased.
  const unresolvedLocalePairCount = displayLocaleCountryPairRows.filter((row) => !!row.localeRef && !row.localeId).length;

  /**
   * Commits one row's edit directly, in response to that exact user action — never from a render
   * effect.
   *
   * A row that is now complete (both fields set) is written straight into countryLocales: an
   * EXISTING row is replaced in place by its own index, so every OTHER entry — resolved or not —
   * passes through untouched; a PENDING row is appended and dropped from local state. A row that is
   * now INCOMPLETE (the user just cleared one of its own fields) is pulled out of countryLocales —
   * that is the user's own edit, not a background drop — and kept visible as a pending row so the
   * in-progress edit is not lost.
   */
  const commitLocaleCountryPairEdit = (
    source: { kind: 'existing'; entryIndex: number } | { kind: 'pending'; key: string },
    next: { localeRef?: ISerializedRevisionReference; country: MonitoringPromptCountry | '' }
  ) => {
    const isComplete = !!next.country && !!next.localeRef;

    if (source.kind === 'existing') {
      const currentCountryLocales = values.countryLocales || [];
      if (isComplete) {
        setValue(
          'countryLocales',
          currentCountryLocales.map((entry, entryIndex) =>
            entryIndex === source.entryIndex ? { country: next.country as MonitoringPromptCountry, locale: next.localeRef! } : entry
          )
        );
        return;
      }

      setValue(
        'countryLocales',
        currentCountryLocales.filter((_entry, entryIndex) => entryIndex !== source.entryIndex)
      );
      // A fresh key, never the entryIndex-derived one: a sibling row can reindex the moment this
      // entry is spliced out, and reusing that derived id here would collide with it.
      setTemplateLocaleCountryPairs((current) => [
        ...current,
        { key: nextPendingPairKey(), localeRef: next.localeRef, country: next.country },
      ]);
      return;
    }

    if (isComplete) {
      setValue('countryLocales', [
        ...(values.countryLocales || []),
        { country: next.country as MonitoringPromptCountry, locale: next.localeRef! },
      ]);
      setTemplateLocaleCountryPairs((current) => current.filter((pair) => pair.key !== source.key));
      return;
    }

    setTemplateLocaleCountryPairs((current) =>
      current.map((pair) => (pair.key === source.key ? { key: pair.key, localeRef: next.localeRef, country: next.country } : pair))
    );
  };

  const selectedEngineIds = React.useMemo(
    () =>
      (values.engines || [])
        .map((reference) => getOptionIdForReference(reference, engineOptionsById, engineOptionsByKey))
        .filter((id): id is string => !!id),
    [engineOptionsById, engineOptionsByKey, values.engines]
  );

  /**
   * Selected template terms whose facet is KNOWN to differ from the chosen template facet.
   *
   * REPORTED, NEVER REMOVED. A render effect used to re-filter the selection against the loaded
   * option map and write the survivors back, so a term whose option had not loaded — options still
   * arriving, paged out, or archived — resolved to undefined, failed the comparison and was
   * dropped with no user action and no warning. When none of a template's terms were in the map at
   * that moment the whole array emptied, and because the client always submits an explicit
   * `templateTerms: []`, saving then told the server to clear them.
   *
   * A term missing from the option map is therefore UNKNOWN, not wrong: it is left alone and
   * submitted unchanged. Only a term whose option IS loaded and whose facet genuinely differs is
   * surfaced here, for the reader to act on.
   */
  const mismatchedTemplateTermNames = React.useMemo(() => {
    const facet = values.templateFacet;
    if (!isTemplate || !facet) {
      return [];
    }

    return (values.templateTerms || [])
      .map((reference) => getOptionIdForReference(reference, templateTermOptionsById, templateTermOptionsByKey))
      .filter((id): id is string => !!id)
      .map((id) => templateTermOptionsById.get(id))
      .filter((option): option is NonNullable<typeof option> => !!option && option.facet !== facet)
      .map((option) => option.name);
  }, [isTemplate, templateTermOptionsById, templateTermOptionsByKey, values.templateFacet, values.templateTerms]);

  const selectedTemplateTermIds = React.useMemo(
    () =>
      (values.templateTerms || [])
        .map((reference) => getOptionIdForReference(reference, templateTermOptionsById, templateTermOptionsByKey))
        .filter((id): id is string => !!id),
    [templateTermOptionsById, templateTermOptionsByKey, values.templateTerms]
  );

  const selectedStaticTermIds = React.useMemo(
    () =>
      (values.staticTerms || [])
        .map((reference) => getOptionIdForReference(reference, templateTermOptionsById, templateTermOptionsByKey))
        .filter((id): id is string => !!id),
    [templateTermOptionsById, templateTermOptionsByKey, values.staticTerms]
  );

  const selectedTermIds = React.useMemo(
    () =>
      (values.terms || [])
        .map((assignment) => getTermReferenceFromAssignment(assignment))
        .map((reference) => getOptionIdForReference(reference, templateTermOptionsById, templateTermOptionsByKey))
        .filter((id): id is string => !!id),
    [templateTermOptionsById, templateTermOptionsByKey, values.terms]
  );

  const selectedPurposeTermIds = React.useMemo(
    () => selectedTermIds.filter((id) => templateTermOptionsById.get(id)?.facet === TaxonomyFacet.MonitoringPromptPurpose),
    [selectedTermIds, templateTermOptionsById]
  );

  const selectedAdministrativeTermIds = React.useMemo(
    () => selectedTermIds.filter((id) => templateTermOptionsById.get(id)?.facet !== TaxonomyFacet.MonitoringPromptPurpose),
    [selectedTermIds, templateTermOptionsById]
  );

  const selectedTargetTermIds = React.useMemo(
    () =>
      (values.targetTerms || [])
        .map((assignment) => getTermReferenceFromAssignment(assignment))
        .map((reference) => getOptionIdForReference(reference, templateTermOptionsById, templateTermOptionsByKey))
        .filter((id): id is string => !!id),
    [templateTermOptionsById, templateTermOptionsByKey, values.targetTerms]
  );

  if (isTemplateManagedInstance) {
    // Every field below is rewritten from the template on its next reconcile, so the backend
    // rejects an edit to any of them here — see assertTemplateManagedInstanceRestUpdateAllowed in
    // sync-core. Rendering them read-only keeps this form from ever offering an edit the save
    // request would just come back and refuse.
    return (
      <>
        <p className="text-xs text-slate-500 mb-3">
          This instance is managed by its template: these fields follow the template automatically and can’t be edited here. Edit the
          template instead to change them.
        </p>

        <div>
          <Form.Label className="fw-bold">Name</Form.Label>
          <Form.Control value={values.name || ''} readOnly />
        </div>

        <div className="mt-3">
          <Form.Label className="fw-bold">Prompt</Form.Label>
          <Form.Control as="textarea" rows={5} value={values.promptText || ''} readOnly />
        </div>

        <div className="mt-3">
          <Form.Label className="fw-bold">Frequency</Form.Label>
          <Form.Select value={String(values.frequencyHours ?? MONITORING_PROMPT_DEFAULT_FREQUENCY_HOURS)} disabled>
            {isCustomFrequency ? <option value={String(values.frequencyHours)}>{`Custom (${values.frequencyHours} h)`}</option> : null}
            {MONITORING_PROMPT_FREQUENCY_OPTIONS.map((option) => (
              <option key={option.hours} value={String(option.hours)}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        </div>

        <div className="mt-3">
          <Form.Label className="fw-bold">Target Engines</Form.Label>
          <div className="rounded border border-slate-200 p-2 max-h-44 overflow-y-auto">
            {engineOptions.map((option) => {
              const optionId = String(option.id);
              return (
                <Form.Check
                  key={optionId}
                  type="checkbox"
                  id={`monitoring-prompt-engine-${optionId}`}
                  label={
                    <>
                      {/* Decoration on the name beside it, and nothing at all for an engine this
                          app carries no mark for. */}
                      <EngineLogo termKey={option.key} className="me-2" />
                      {option.name}
                    </>
                  }
                  checked={selectedEngineIds.includes(optionId)}
                  disabled
                />
              );
            })}
          </div>
        </div>

        <Right className="pe-1 mt-4">
          <button
            type="submit"
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            disabled={saving}
          >
            {submitLabel}
          </button>
        </Right>
      </>
    );
  }

  return (
    <>
      <div>
        <Form.Label className="fw-bold">Name</Form.Label>
        <Form.Control value={values.name || ''} onChange={(event) => setValue('name', event.target.value)} />
      </div>

      <div className="mt-3">
        <Form.Label className="fw-bold">Prompt</Form.Label>
        <Form.Control
          as="textarea"
          rows={5}
          value={values.promptText || ''}
          onChange={(event) => setValue('promptText', event.target.value)}
        />
        {templatePromptTextChanged ? (
          <p className="text-xs text-rose-600 mt-1">
            Changing this template prompt text will update all instances. Manual instance text edits will be lost.
          </p>
        ) : null}
        {templateGuidanceText ? <p className="text-xs text-slate-500 mt-1">{templateGuidanceText}</p> : null}
      </div>

      <div className="mt-3">
        <div>
          <Form.Label className="fw-bold">Prompt Locale</Form.Label>
          <Form.Select
            value={promptLocaleId}
            onChange={(event) => {
              const selectedOption = localeOptionsById.get(String(event.target.value));
              setValue('promptLocale', revisionReferenceFromOption(selectedOption));
            }}
          >
            <option value="">Select locale</option>
            {localeOptions.map((option) => (
              <option key={option.id} value={String(option.id)}>
                {option.name}
              </option>
            ))}
          </Form.Select>
        </div>
      </div>

      <div className="mt-3">
        <div>
          <Form.Label className="fw-bold">Frequency</Form.Label>
          <Form.Select
            value={String(values.frequencyHours ?? MONITORING_PROMPT_DEFAULT_FREQUENCY_HOURS)}
            onChange={(event) => setValue('frequencyHours', Number(event.target.value))}
          >
            {/* A stored value outside the table is offered as-is rather than snapped to a
                neighbour, so opening the form cannot change how often a prompt runs. */}
            {isCustomFrequency ? <option value={String(values.frequencyHours)}>{`Custom (${values.frequencyHours} h)`}</option> : null}
            {MONITORING_PROMPT_FREQUENCY_OPTIONS.map((option) => (
              <option key={option.hours} value={String(option.hours)}>
                {option.label}
              </option>
            ))}
          </Form.Select>
          <p className="text-xs text-slate-500 mt-1">
            {isTemplate
              ? 'Instances generated from this template follow this frequency.'
              : 'Instances managed by a template follow the template’s frequency.'}
          </p>
        </div>
      </div>

      <div className="mt-3">
        <Form.Label className="fw-bold">Templating</Form.Label>
        <Form.Check
          type="checkbox"
          id="monitoring-prompt-is-template"
          label="Is Template"
          checked={isTemplate}
          disabled={lockType}
          onChange={(event) => setValue('type', event.target.checked ? MonitoringPromptType.Template : MonitoringPromptType.Instance)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
        <div>
          <Form.Label className="fw-bold">Target Engines</Form.Label>
          <div className="rounded border border-slate-200 p-2 max-h-44 overflow-y-auto">
            {engineOptions.map((option) => {
              const optionId = String(option.id);
              const checked = selectedEngineIds.includes(optionId);

              return (
                <Form.Check
                  key={optionId}
                  type="checkbox"
                  id={`monitoring-prompt-engine-${optionId}`}
                  label={
                    <>
                      {/* Decoration on the name beside it, and nothing at all for an engine this
                          app carries no mark for. */}
                      <EngineLogo termKey={option.key} className="me-2" />
                      {option.name}
                    </>
                  }
                  checked={checked}
                  onChange={(event) => {
                    // Written straight against values.engines — never rebuilt from
                    // selectedEngineIds, which is already filtered down to the engines that
                    // resolve. Rebuilding from it here would silently drop any engine whose
                    // option has not loaded the moment a DIFFERENT engine is toggled.
                    const currentEngines = values.engines || [];
                    if (event.target.checked) {
                      if (checked) {
                        return;
                      }

                      const reference = revisionReferenceFromOption(option);
                      if (reference) {
                        setValue('engines', [...currentEngines, reference]);
                      }
                      return;
                    }

                    setValue(
                      'engines',
                      removeEntryById(currentEngines, optionId, (reference) => reference, engineOptionsById, engineOptionsByKey)
                    );
                  }}
                />
              );
            })}
          </div>
        </div>

        <div className="md:col-span-2">
          <Form.Label className="fw-bold">Target Locale/Country {isInstance ? 'Pair' : 'Pairs'}</Form.Label>
          {/* Named, not removed: a pair whose locale option has not loaded is kept exactly as
              stored, so nothing is lost by opening this page before every option has arrived. */}
          {unresolvedLocalePairCount ? (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
              {unresolvedLocalePairCount === 1 ? 'One locale/country pair uses' : `${unresolvedLocalePairCount} locale/country pairs use`} a
              locale that has not finished loading. They are kept as they are.
            </p>
          ) : null}
          <div className="space-y-2">
            {displayLocaleCountryPairRows.map((pair) => (
              <div
                key={pair.key}
                className={`grid grid-cols-1 ${isInstance ? 'md:grid-cols-2' : 'md:grid-cols-[1fr_1fr_auto]'} gap-2 items-end`}
              >
                <div>
                  <Form.Label className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500">
                    Locale
                  </Form.Label>
                  <Form.Select
                    value={pair.localeRef && !pair.localeId ? UNRESOLVED_LOCALE_OPTION_VALUE : pair.localeId}
                    onChange={(event) => {
                      const nextLocaleId = String(event.target.value || '');
                      const option = localeOptionsById.get(nextLocaleId);
                      commitLocaleCountryPairEdit(pair.source, {
                        localeRef: revisionReferenceFromOption(option),
                        country: pair.country,
                      });
                    }}
                  >
                    <option value="">Select locale</option>
                    {/* The pair's real reference has no loaded option yet — offered as-is (under a
                        value distinct from the blank placeholder above, and disabled so it is never
                        itself a choice) rather than left to fall back to that placeholder, which
                        would read as "nothing selected" even though a real value is being kept. */}
                    {pair.localeRef && !pair.localeId ? (
                      <option value={UNRESOLVED_LOCALE_OPTION_VALUE} disabled>
                        {`Locale not loaded yet (${getRevisionReferenceKey(pair.localeRef) || getRevisionReferenceId(pair.localeRef)}) — kept`}
                      </option>
                    ) : null}
                    {localeOptions.map((option) => (
                      <option key={option.id} value={String(option.id)}>
                        {option.name}
                      </option>
                    ))}
                  </Form.Select>
                </div>

                <div>
                  <Form.Label className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500">
                    Country
                  </Form.Label>
                  <Form.Select
                    value={pair.country}
                    onChange={(event) => {
                      const nextCountry = (event.target.value || '') as MonitoringPromptCountry | '';
                      commitLocaleCountryPairEdit(pair.source, {
                        localeRef: pair.localeRef,
                        country: nextCountry,
                      });
                    }}
                  >
                    <option value="">Select country</option>
                    {/* Same idea as the locale fallback above: a stored country not currently in
                        the offered list is shown and kept rather than silently blanked. */}
                    {pair.country && !countrySelectOptions.some((option) => option.value === pair.country) ? (
                      <option value={pair.country}>{`${translateEnumValue('MonitoringPromptCountry', pair.country)} (unavailable)`}</option>
                    ) : null}
                    {countrySelectOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </div>

                {!isInstance ? (
                  <button
                    type="button"
                    className="h-[38px] px-3 py-1.5 text-xs rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    disabled={displayLocaleCountryPairRows.length <= 1}
                    onClick={() => {
                      if (pair.source.kind === 'existing') {
                        const entryIndex = pair.source.entryIndex;
                        setValue(
                          'countryLocales',
                          (values.countryLocales || []).filter((_entry, currentIndex) => currentIndex !== entryIndex)
                        );
                        return;
                      }

                      setTemplateLocaleCountryPairs((current) => current.filter((currentPair) => currentPair.key !== pair.key));
                    }}
                  >
                    Remove
                  </button>
                ) : null}
              </div>
            ))}

            {!isInstance ? (
              <button
                type="button"
                className={BP_TOOLBAR_BUTTON_SMALL_CLASSES}
                onClick={() =>
                  setTemplateLocaleCountryPairs((current) => [...current, { key: nextPendingPairKey(), localeRef: undefined, country: '' }])
                }
              >
                Add pair
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {isTemplate ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <div>
            <Form.Label className="fw-bold">Template Terms</Form.Label>
            <Form.Select
              value={values.templateFacet || ''}
              onChange={(event) => {
                const nextFacet = event.target.value as TaxonomyFacet | '';
                if (nextFacet) {
                  setValue('templateFacet', nextFacet);
                  return;
                }

                // CLEARING ASSIGNS null, NEVER undefined. JSON.stringify drops an undefined key, and
                // the server merges the update over the stored prompt, so an omitted field reads as
                // "unchanged" and the facet would never actually clear. The terms go with it: they
                // only mean anything under a facet.
                setValue('templateFacet', null);
                setValue('templateTerms', []);
              }}
            >
              <option value="">Select taxonomy</option>
              {TEMPLATE_FACETS.map((facet) => (
                <option key={facet} value={facet}>
                  {translateEnumValue('TaxonomyFacet', facet)}
                </option>
              ))}
            </Form.Select>
            {templateGuidanceText ? <p className="text-xs text-slate-500 mt-1">{templateGuidanceText}</p> : null}
          </div>

          {selectedTemplateFacet ? (
            <div>
              <Form.Label className="fw-bold">Template Terms</Form.Label>
              {/* Named, not removed: the form reports a real facet mismatch and leaves the
                  selection exactly as loaded, so nothing is lost by opening the page. */}
              {mismatchedTemplateTermNames.length ? (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
                  {mismatchedTemplateTermNames.length === 1 ? 'This term does' : 'These terms do'} not match the selected facet:{' '}
                  {mismatchedTemplateTermNames.join(', ')}. They are kept as they are — remove them here if that is what you intend.
                </p>
              ) : null}
              <div className="d-flex flex-wrap gap-2 mb-2">
                {selectedTemplateTermIds.map((termId) => {
                  const option = templateTermOptionsById.get(termId);
                  if (!option || option.facet !== selectedTemplateFacet) {
                    return null;
                  }

                  return (
                    <TaxonomyTermNameBadge
                      key={termId}
                      api={api}
                      termKey={option.key}
                      applyFacetPresentation
                      termFacet={option.facet}
                      onRemove={() =>
                        // Filters the ORIGINAL array, not selectedTemplateTermIds: that list is
                        // already narrowed to terms that resolve, so rebuilding from it would
                        // silently drop any co-selected term whose option has not loaded.
                        setValue(
                          'templateTerms',
                          removeEntryById(
                            values.templateTerms || [],
                            termId,
                            (reference) => reference,
                            templateTermOptionsById,
                            templateTermOptionsByKey
                          )
                        )
                      }
                    />
                  );
                })}
              </div>

              <TaxonomyTermTwoStepDropdown
                taxonomies={templateTermTaxonomies.filter((taxonomy) => taxonomy.facet === selectedTemplateFacet)}
                terms={templateTermDropdownOptions.filter((term) => term.facet === selectedTemplateFacet)}
                onSelectTerm={(termId) => {
                  if (selectedTemplateTermIds.includes(termId)) {
                    return;
                  }

                  const option = templateTermOptionsById.get(termId);
                  const reference = option && revisionReferenceFromOption(option);
                  if (reference) {
                    setValue('templateTerms', [...(values.templateTerms || []), reference]);
                  }
                }}
                buttonLabel="Add Term"
                startAtTermsForFacet={selectedTemplateFacet}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="mt-3">
        <Form.Label className="fw-bold">
          Purpose<span className="text-rose-600">*</span>
        </Form.Label>
        <div className="d-flex flex-wrap gap-2 mb-2">
          {selectedPurposeTermIds.map((termId) => {
            const option = templateTermOptionsById.get(termId);
            if (!option) {
              return null;
            }

            return (
              <TaxonomyTermNameBadge
                key={termId}
                api={api}
                termKey={option.key}
                applyFacetPresentation
                termFacet={option.facet}
                onRemove={() =>
                  // Filters the ORIGINAL terms array rather than rebuilding it from the two
                  // already-resolved id lists, which would silently drop any purpose OR
                  // administrative term whose option has not loaded.
                  setValue(
                    'terms',
                    removeEntryById(
                      values.terms || [],
                      termId,
                      (assignment) => getTermReferenceFromAssignment(assignment),
                      templateTermOptionsById,
                      templateTermOptionsByKey
                    )
                  )
                }
              />
            );
          })}
        </div>

        <TaxonomyTermTwoStepDropdown
          taxonomies={purposeTaxonomies}
          terms={purposeTermOptions}
          onSelectTerm={(termId) => {
            if (selectedPurposeTermIds.includes(termId)) {
              return;
            }

            const option = templateTermOptionsById.get(termId);
            const assignment = option && termAssignmentFromOption(option);
            if (assignment) {
              setValue('terms', [...(values.terms || []), assignment]);
            }
          }}
          buttonLabel="Add Purpose"
          startAtTermsForFacet={TaxonomyFacet.MonitoringPromptPurpose}
        />
        <p className="text-xs text-slate-500 mt-1">
          At least one purpose is required. AI Visibility analytics only aggregate prompts that share the same purpose.
        </p>
      </div>

      <div className="mt-3">
        <Form.Label className="fw-bold">Administrative prompt tags</Form.Label>
        <div className="d-flex flex-wrap gap-2 mb-2">
          {selectedAdministrativeTermIds.map((termId) => {
            const option = templateTermOptionsById.get(termId);
            if (!option) {
              return null;
            }

            return (
              <TaxonomyTermNameBadge
                key={termId}
                api={api}
                termKey={option.key}
                applyFacetPresentation
                termFacet={option.facet}
                onRemove={() =>
                  // Same rule as the Purpose badge above: filter the original array, never rebuild
                  // it from the resolved id lists.
                  setValue(
                    'terms',
                    removeEntryById(
                      values.terms || [],
                      termId,
                      (assignment) => getTermReferenceFromAssignment(assignment),
                      templateTermOptionsById,
                      templateTermOptionsByKey
                    )
                  )
                }
              />
            );
          })}
        </div>

        <TaxonomyTermTwoStepDropdown
          taxonomies={administrativeTaxonomies}
          terms={administrativeTermOptions}
          onSelectTerm={(termId) => {
            if (selectedAdministrativeTermIds.includes(termId)) {
              return;
            }

            const option = templateTermOptionsById.get(termId);
            const assignment = option && termAssignmentFromOption(option);
            if (assignment) {
              setValue('terms', [...(values.terms || []), assignment]);
            }
          }}
          buttonLabel="Add Term"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        <div>
          <Form.Label className="fw-bold">Static result terms</Form.Label>
          <div className="d-flex flex-wrap gap-2 mb-2">
            {selectedStaticTermIds.map((termId) => {
              const option = templateTermOptionsById.get(termId);
              if (!option) {
                return null;
              }

              return (
                <TaxonomyTermNameBadge
                  key={termId}
                  api={api}
                  termKey={option.key}
                  applyFacetPresentation
                  termFacet={option.facet}
                  onRemove={() =>
                    // Filters the ORIGINAL array — see the templateTerms badge above for why
                    // rebuilding from selectedStaticTermIds would drop an unresolved sibling.
                    setValue(
                      'staticTerms',
                      removeEntryById(
                        values.staticTerms || [],
                        termId,
                        (reference) => reference,
                        templateTermOptionsById,
                        templateTermOptionsByKey
                      )
                    )
                  }
                />
              );
            })}
          </div>

          <TaxonomyTermTwoStepDropdown
            taxonomies={staticTaxonomies}
            terms={staticTermOptions}
            onSelectTerm={(termId) => {
              if (selectedStaticTermIds.includes(termId)) {
                return;
              }

              const option = templateTermOptionsById.get(termId);
              const reference = option && revisionReferenceFromOption(option);
              if (reference) {
                setValue('staticTerms', [...(values.staticTerms || []), reference]);
              }
            }}
            buttonLabel="Add Term"
          />
          <p className="text-xs text-slate-500 mt-1">Always attached to results from this prompt.</p>
        </div>

        <div>
          <Form.Label className="fw-bold">Extracted result terms</Form.Label>
          <div className="d-flex flex-wrap gap-2 mb-2">
            {selectedTargetTermIds.map((termId) => {
              const option = templateTermOptionsById.get(termId);
              if (!option) {
                return null;
              }

              return (
                <TaxonomyTermNameBadge
                  key={termId}
                  api={api}
                  termKey={option.key}
                  applyFacetPresentation
                  termFacet={option.facet}
                  onRemove={() =>
                    // Filters the ORIGINAL array — see the templateTerms badge above for why
                    // rebuilding from selectedTargetTermIds would drop an unresolved sibling.
                    setValue(
                      'targetTerms',
                      removeEntryById(
                        values.targetTerms || [],
                        termId,
                        (assignment) => getTermReferenceFromAssignment(assignment),
                        templateTermOptionsById,
                        templateTermOptionsByKey
                      )
                    )
                  }
                />
              );
            })}
          </div>

          <TaxonomyTermTwoStepDropdown
            taxonomies={targetTaxonomies}
            terms={targetTermOptions}
            onSelectTerm={(termId) => {
              if (selectedTargetTermIds.includes(termId)) {
                return;
              }

              const option = templateTermOptionsById.get(termId);
              const assignment = option && termAssignmentFromOption(option);
              if (assignment) {
                setValue('targetTerms', [...(values.targetTerms || []), assignment]);
              }
            }}
            buttonLabel="Add Term"
          />
          <p className="text-xs text-slate-500 mt-1">
            Candidate terms to match from extraction output.{' '}
            <strong>For comparison prompts, include all your relevant products and brands that should be looked for.</strong> Do not include
            templated terms.
          </p>
          {extractionHintNames.length ? (
            <p className="text-xs text-slate-500 mt-1">
              {formatFacetNameList(extractionHintNames)} are read from every answer automatically, so you do not need to add them here.
            </p>
          ) : null}
        </div>
      </div>

      <Right className="pe-1 mt-4">
        <button
          type="submit"
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          disabled={saving}
        >
          {submitLabel}
        </button>
      </Right>
    </>
  );
}

export function ensureMonitoringPromptDraftDefaults(draft: ClientMonitoringPromptDraft) {
  if (!draft.status) draft.status = MonitoringPromptStatus.Active;
  if (!draft.type) draft.type = MonitoringPromptType.Instance;
  if (!draft.frequencyHours) draft.frequencyHours = 24;
  if (!(draft as unknown as { countryLocales?: unknown[] }).countryLocales) {
    (draft as unknown as { countryLocales: unknown[] }).countryLocales = [];
  }
  if (!draft.engines) draft.engines = [];
  if (!draft.templateTerms) draft.templateTerms = [];
  if (!draft.staticTerms) draft.staticTerms = [];
  if (!draft.terms) draft.terms = [];
  if (!draft.targetTerms) draft.targetTerms = [];
  if (!draft.name) draft.name = '';
  if (!draft.promptText) draft.promptText = '';
}

export function ensureMonitoringPromptEntityDefaults(entity: ClientMonitoringPromptEntity) {
  if (!(entity as unknown as { countryLocales?: unknown[] }).countryLocales) {
    (entity as unknown as { countryLocales: unknown[] }).countryLocales = [];
  }
  if (!entity.engines) entity.engines = [];
  if (!entity.templateTerms) entity.templateTerms = [];
  if (!entity.staticTerms) entity.staticTerms = [];
  if (!entity.terms) entity.terms = [];
  if (!entity.targetTerms) entity.targetTerms = [];
}

export function MonitoringPromptDraftEditor({
  api,
  initialValues,
  submitLabel,
  taxonomyOptions,
  localeOptions,
  availableCountries,
  engineOptions,
  templateTermOptions,
  onSubmit,
}: {
  api?: ISyncCoreApi;
  initialValues: ClientMonitoringPromptDraft;
  submitLabel: string;
  taxonomyOptions: ITaxonomyDropdownOption[];
  localeOptions: IMonitoringTermOption[];
  availableCountries?: MonitoringPromptCountry[];
  engineOptions: IMonitoringTermOption[];
  templateTermOptions: IMonitoringTermOption[];
  onSubmit: (draft: ClientMonitoringPromptDraft) => Promise<void>;
}) {
  const [saving, setSaving] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<unknown>(undefined);

  return (
    <MonitoringPromptDraftForm
      initialValues={initialValues}
      validate={validateMonitoringPromptForm}
      onSubmit={async (draft) => {
        setSaving(true);
        setSubmitError(undefined);

        try {
          await onSubmit(draft);
        } catch (error) {
          setSubmitError(error);
        } finally {
          setSaving(false);
        }
      }}
    >
      {({ values, setValue }) => (
        <>
          {submitError ? (
            <Alert variant="danger" className="mb-3">
              <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                {getSubmitErrorMessage(submitError)}
              </pre>
            </Alert>
          ) : null}

          <PromptFormBody
            api={api}
            values={values}
            initialPromptText={initialValues.promptText || ''}
            setValue={setValue}
            taxonomyOptions={taxonomyOptions}
            localeOptions={localeOptions}
            availableCountries={availableCountries || ALL_COUNTRIES}
            engineOptions={engineOptions}
            templateTermOptions={templateTermOptions}
            submitLabel={submitLabel}
            saving={saving}
          />
        </>
      )}
    </MonitoringPromptDraftForm>
  );
}

export function MonitoringPromptEntityEditor({
  api,
  initialValues,
  submitLabel,
  taxonomyOptions,
  localeOptions,
  availableCountries,
  engineOptions,
  templateTermOptions,
  onSubmit,
}: {
  api?: ISyncCoreApi;
  initialValues: ClientMonitoringPromptEntity;
  submitLabel: string;
  taxonomyOptions: ITaxonomyDropdownOption[];
  localeOptions: IMonitoringTermOption[];
  availableCountries?: MonitoringPromptCountry[];
  engineOptions: IMonitoringTermOption[];
  templateTermOptions: IMonitoringTermOption[];
  onSubmit: (entity: ClientMonitoringPromptEntity) => Promise<void>;
}) {
  const [saving, setSaving] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<unknown>(undefined);

  return (
    <MonitoringPromptEntityForm
      initialValues={initialValues}
      validate={validateMonitoringPromptForm}
      onSubmit={async (entity) => {
        setSaving(true);
        setSubmitError(undefined);

        try {
          await onSubmit(entity);
        } catch (error) {
          setSubmitError(error);
        } finally {
          setSaving(false);
        }
      }}
    >
      {({ values, setValue }) => (
        <>
          {submitError ? (
            <Alert variant="danger" className="mb-3">
              <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                {getSubmitErrorMessage(submitError)}
              </pre>
            </Alert>
          ) : null}

          <PromptFormBody
            api={api}
            values={values}
            initialPromptText={initialValues.promptText || ''}
            setValue={setValue}
            taxonomyOptions={taxonomyOptions}
            localeOptions={localeOptions}
            availableCountries={availableCountries || ALL_COUNTRIES}
            engineOptions={engineOptions}
            templateTermOptions={templateTermOptions}
            submitLabel={submitLabel}
            saving={saving}
            lockType
          />
        </>
      )}
    </MonitoringPromptEntityForm>
  );
}
